import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, FileSignature, Eye, Gauge } from "lucide-react";
import { HID } from "@/constants/cosecIds";

const PILLARS = [
  { Icon: Users, title: "Directors & secretaries", desc: "Roles, appointments and resignations tracked end-to-end." },
  { Icon: FileSignature, title: "Resolutions & minutes", desc: "Draft, sign and archive board decisions in one click." },
  { Icon: Eye, title: "Ownership changes", desc: "PSC events and shareholder updates with audit-grade history." },
  { Icon: Gauge, title: "Risk & oversight", desc: "Compliance score per entity, with drill-down actions." },
];

export default function Governance() {
  return (
    <section className="py-20 md:py-28">
      <div className="container-cv grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <span className="section-eyebrow">Corporate governance &amp; board control</span>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-bold text-primary tracking-tight leading-[1.08]">
            Strengthen Corporate Governance and{" "}
            <span className="text-accent">Board Control.</span>
          </h2>
          <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl">
            Give directors and company secretaries a clear view of company records,
            ownership changes, filing obligations, and key actions. CoSec helps
            businesses improve oversight, reduce risk, and maintain strong governance
            standards from one secure platform.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {PILLARS.map(({ Icon, title, desc }, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="h-10 w-10 grid place-items-center rounded-lg bg-accent/15 text-primary shrink-0">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-display font-bold text-primary">{title}</div>
                  <div className="text-sm text-muted-foreground leading-relaxed">{desc}</div>
                </div>
              </div>
            ))}
          </div>

          <Button
            asChild
            data-testid={HID.ctaGovernance}
            className="mt-8 h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-semibold shadow-[0_8px_24px_-6px_rgba(124,194,66,0.55)]"
          >
            <a href="/contact">
              Book a Demo <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>

        <div className="lg:col-span-6">
          <div className="relative rounded-3xl overflow-hidden border border-border shadow-[0_30px_80px_-30px_rgba(14,58,95,0.35)]">
            <img
              src="https://images.unsplash.com/photo-1573166364524-d9dbfd8bbf83?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
              alt="Boardroom governance"
              className="h-full w-full object-cover aspect-[5/4]"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/70 via-primary/20 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 rounded-2xl bg-white/95 backdrop-blur p-5">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-accent">Board snapshot</div>
              <div className="mt-1 font-display text-lg font-bold text-primary">All actions tracked & signed</div>
              <div className="mt-3 grid grid-cols-3 gap-3">
                {[
                  { k: "12", l: "Meetings" },
                  { k: "47", l: "Resolutions" },
                  { k: "100%", l: "Signed off" },
                ].map((s, i) => (
                  <div key={i} className="rounded-md bg-secondary/60 p-2.5 text-center">
                    <div className="font-display text-base font-bold text-primary">{s.k}</div>
                    <div className="text-[10px] text-muted-foreground">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
